import { Injectable } from '@angular/core';
import { CheckinBaseClass } from '../../components';
import { LOAD_MASTERS } from '../../master/master';
import { AswMasterService, SystemDateService } from '@lib/services';
import { zip } from 'rxjs';
import { take } from 'rxjs/operators';
import { AirlineI18nJoinData } from '../../master/airline-i18n-join-by-airline-code';
import { MileageProgramI18NData } from '../../master/mileage-program-i18n-by-carrier-code';
import {
  PostJourneysGetJourneyResponseDataFlightsInner,
  PostJourneysGetJourneyResponseDataTravelersInnerFirstFlightAcceptanceEligibility,
} from 'src/sdk_checkin';
import { FunctionIneligibleReasonData } from '../../master/function-ineligible-reason';
import { PosCountryAllData } from '../../master/pos-country-all';
import { passportCountryPulldown } from '../../interface/passport-country-pulldown';
import { CountryCountryI18nAllData } from '../../master/country-countryI18n-all';
import {
  VListDataCheckinCainClassBySearchKey,
  VListDataCheckinCainClassData,
} from '../../master/v-list-data-checkin-cabin-class-by-search-key';

@Injectable({
  providedIn: 'root',
})
export class MasterDataService extends CheckinBaseClass {
  // 現在時刻を適用日と比較できるようにするためのフォーマット
  private readonly DATE_FORMAT = {
    YMD: 'yyyyMMdd',
  };

  constructor(private _aswMasterSvc: AswMasterService, private _systemDateSvc: SystemDateService) {
    super();
  }

  init() {}

  override destroy(): void {}

  /**
   * キャリアマスターから適用日を考慮してキャリアURLを取得する
   * 注：キャリアマスターのキャッシュが読み込まれていない段階でメソッドを呼ぶと空文字列が返却される
   * @param operatingAirlineCode　運行キャリアコード
   * @param departureDate セグメントの出発日時
   * @returns 指定された運行キャリアのキャリアURL
   */
  public getOperatingAirlineLink(operatingAirlineCode: string, departureDate: string): string {
    // セグメントの出発時刻をyyyyMMdd表記の数値型にしたもの
    const departureDateNumber = Number(this.formatDepartureDateCanCompare(departureDate));
    // キャリアURL
    let url = '';
    this.subscribeService(
      this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.AIRLINE_I18NJOIN_BY_AIRLINE_CODE.key).pipe(take(1)),
      (carrierMaster) => {
        // 指定したキャリアコードから対象となるレコードのリストを引き当てる
        // 適用日が存在するので、適用日の種類分の要素数になる
        const carrierList = carrierMaster[operatingAirlineCode];
        if (!carrierList) {
          return;
        }

        // 適用日ごとに繰り返して、現在時刻が適用開始日から適用終了日までの間であればその要素のキャリアURLを設定する
        carrierList.forEach((carrierRecord: AirlineI18nJoinData) => {
          // 適用日はyyyyMMdd形式なのでnumber型に変換して8桁の数値として比較
          const apply_from_date = Number(carrierRecord.apply_from_date);
          const apply_to_date = Number(carrierRecord.apply_to_date);
          if (apply_from_date <= departureDateNumber && departureDateNumber <= apply_to_date) {
            url = carrierRecord.operating_airline_url;
            return;
          }
        });
      }
    );
    return url;
  }

  /**
   * マイレージプログラムマスターから適用日を考慮して、国際化されたマイレージプログラム名を取得する
   * 注：マイレージプログラムマスターのキャッシュが読み込まれていない段階でメソッドを呼ぶと空文字列が返却される
   * @param carrierCode　キャリアコード
   * @returns 国際化されたマイレージプログラム名称
   */
  public getMileageProgramName(carrierCode: string): string {
    // 現在時刻
    const nowDate = Number(this._systemDateSvc.getFormattedSystemDate(this.DATE_FORMAT.YMD));
    // 国際化されたマイレージプログラム名称
    let mileageProgramName = '';
    this.subscribeService(
      this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.MILEAGE_PROGRAM_BY_CARRIER_CODE.key).pipe(take(1)),
      (mileageMaster) => {
        // 指定したキャリアコードから対象となるレコードのリストを引き当てる
        // 適用日が存在するので、適用日の種類分の要素数になる
        const mileageList = mileageMaster[carrierCode];
        if (!mileageList) {
          return;
        }

        // 適用日ごとに繰り返して、現在時刻が適用開始日から適用終了日までの間であればその要素のキャリア名を表示するためのkeyを設定する
        mileageList.forEach((mileageRecord: MileageProgramI18NData) => {
          // 適用日はyyyyMMdd形式なのでnumber型に変換して8桁の数値として比較
          const apply_from_date = Number(mileageRecord.apply_from_date);
          const apply_to_date = Number(mileageRecord.apply_to_date);
          if (apply_from_date <= nowDate && nowDate <= apply_to_date) {
            mileageProgramName = mileageRecord.mileage_program_name;
          }
        });
      }
    );
    return mileageProgramName;
  }

  /**
   * 受け取ったキャビンクラスコードと国内際区分からキャビンクラス名を取得する
   * 注：キャビンのキャッシュが読み込まれていない段階でメソッドを呼ぶと空文字列が返却される
   * @param cabinCode キャビンクラス
   * @param routeType 国内際区分
   * @returns キャビンクラスの名称
   */
  public getCabinClassName(cabinCode: string, routeType: string) {
    let cabinClassName = '';
    this.subscribeService(
      this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.V_LIST_DATA_CABIN_CLASS_BY_SEARCH_KEY.key).pipe(take(1)),
      (cabinMaster: VListDataCheckinCainClassBySearchKey) => {
        // キャビンコードを引き当てるキーの作成
        // 'R-' + 国内際区分 +'-'+ キャビンクラス
        const cabinCodeKey = ['R', routeType, cabinCode].join('-');
        const cabinList: VListDataCheckinCainClassData[] = cabinMaster[cabinCodeKey];

        // キャビンテーブルに該当のキャビンが存在しなければ何も設定せず抜ける
        if (!cabinList) {
          return;
        }
        cabinClassName = cabinList[0].label || '';
      }
    );
    return cabinClassName;
  }

  /**
   * 受け取ったキャビンクラスコードからキャビンクラス名を表示するための静的文言キーを取得する
   * 注：機能不可事由マスターを機能、ASW不可事由、DxAPI不可事由の順で検索したキャッシュが読み込まれていない段階でメソッドを呼ぶと空文字列が返却される
   * @param firstFlightAcceptanceEligibility 1便目のチェックイン可否判定オブジェクト
   * @returns チェックイン不可事由案内文言キー
   */
  public getCheckinIneligibleReasonKey(
    firstFlightAcceptanceEligibility: PostJourneysGetJourneyResponseDataTravelersInnerFirstFlightAcceptanceEligibility,
    firstFlight: PostJourneysGetJourneyResponseDataFlightsInner
  ) {
    const reason = firstFlightAcceptanceEligibility.reason;
    const dxApiReasons = firstFlightAcceptanceEligibility.dxapiReasons;

    let reasonMessageKey = '';
    this.subscribeService(
      this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.FUNCTION_INELIGIBLE_REASON.key).pipe(take(1)),
      (functionIneligibleReason) => {
        // reasonが存在しない場合は、Keyは存在しない
        if (!reason) {
          return;
        }

        // 機能不可事由キャッシュを機能=checkinで絞り込み
        const reasonByFunction = functionIneligibleReason['checkin'];
        // 機能不可事由キャッシュを機能=checkinで絞り込んだキャッシュをreasonで絞り込み
        const reasonByAswIneligibleReason = reasonByFunction[reason];

        // DxAPI起因のチェックイン不可であるか(ASW不可事由がdxapiAcceptanceEligibility)
        const isCauseDxApi =
          reason ===
          PostJourneysGetJourneyResponseDataTravelersInnerFirstFlightAcceptanceEligibility.ReasonEnum
            .DxapiAcceptanceEligibility;

        // DxAPI起因のチェックイン不可の場合は、dxApiReasonsを繰り返して最初にチェックイン不可事由案内文言キーが存在する不可事由のチェックイン不可事由案内文言キーを設定する
        if (isCauseDxApi && dxApiReasons) {
          for (const dxApiReason of dxApiReasons) {
            // dxApiReasonがflightAcceptanceStatusである場合の文言の取得
            if (dxApiReason === 'flightAcceptanceStatus') {
              const messageKeyCauseFlightAcceptanceStatus = this.getMessageKeyCauseFlightAcceptanceStatus(firstFlight);
              if (messageKeyCauseFlightAcceptanceStatus) {
                reasonMessageKey = messageKeyCauseFlightAcceptanceStatus;
                return;
              }
              // flightAcceptanceStatusで条件に合致しない場合は、次の要素を見る
              continue;
            }

            // dxApiReasonがflightGeneralStatusである場合の文言の取得
            if (dxApiReason === 'flightGeneralStatus') {
              const messageKeyCauseCauseFlightGeneralStatus = this.getMessageKeyCauseFlightGeneralStatus(firstFlight);
              if (messageKeyCauseCauseFlightGeneralStatus) {
                reasonMessageKey = messageKeyCauseCauseFlightGeneralStatus;
                return;
              }
              // flightGeneralStatusで条件に合致しない場合は、次の要素を見る
              continue;
            }

            // dxApiReasonが上記以外だった場合の文言の取得
            const reasonRecord: FunctionIneligibleReasonData = reasonByAswIneligibleReason[dxApiReason]?.[0];
            // メッセージキーがマスタから取得できればその文言を設定
            if (reasonRecord?.checkin_ineligible_reason_message_key) {
              reasonMessageKey = reasonRecord.checkin_ineligible_reason_message_key;
              return;
            }
          }
          return;
        } else {
          // ASW不可事由がdxapiAcceptanceEligibility以外の場合はDxAPI不可事由固定値'-'でレコードを取得し、チェックイン不可事由案内文言キーを設定する
          const reasonRecord: FunctionIneligibleReasonData = reasonByAswIneligibleReason['-']?.[0];
          if (reasonRecord?.checkin_ineligible_reason_message_key) {
            reasonMessageKey = reasonRecord.checkin_ineligible_reason_message_key;
          }
        }
      }
    );
    return reasonMessageKey;
  }

  /**
   * dxApiReasonがflightAcceptanceStatusであるときは、追加で条件を確認して、表示すべき文言キーを返却する
   * @param dxApiReason チェックイン不可理由
   * @param firstFlight 1便目のセグメント情報
   * @returns dxApiReasonがflightAcceptanceStatusとセグメントの条件を判断し条件に合致すれば文言キー、合致しなければundefinedを返却する
   */
  private getMessageKeyCauseFlightAcceptanceStatus(firstFlight: PostJourneysGetJourneyResponseDataFlightsInner) {
    const isSuspended = firstFlight.acceptanceStatus === 'suspended';
    const isClosing = firstFlight.acceptanceStatus === 'gated';
    const isAirCraftChange = firstFlight.isAircraftChanged;

    if ((isSuspended || isClosing) && isAirCraftChange) {
      return 'message.unableToCheckInByAircraftChanged';
    }
    if (isSuspended && !isAirCraftChange) {
      return 'message.unableToCheckInByOtherReasons';
    }
    if (isClosing && !isAirCraftChange) {
      return 'message.unableToCheckInByAcceptanseClosed';
    }
    return undefined;
  }

  /**
   * dxApiReasonがflightGeneralStatusであるときは、追加で条件を確認して、表示すべき文言キーを返却する
   * @param dxApiReason チェックイン不可理由
   * @param firstFlight 1便目のセグメント情報
   * @returns dxApiReasonがflightGeneralStatusかつ1便目の受付ステータスがsuspendedの場合に文言キー、そうでない場合はundefinedを返す
   */
  private getMessageKeyCauseFlightGeneralStatus(firstFlight: PostJourneysGetJourneyResponseDataFlightsInner) {
    if (firstFlight.status === 'suspended') {
      return 'message.unableToCheckInByFlightGeneralStatus';
    }
    return undefined;
  }

  /*
   * 指定されたPOS国コードに属する国情報を返却する
   * もし、POS国コードがJP(日本でない場合)、加えて日本の情報も含まれるリストで返却する
   * 注：POS_国_国国際_全リストが読み込まれていない状態で呼び出すと空の配列を返す
   * @param posCountryCode POS国コード
   */
  public getCountriesBelongToPosCountry(posCountryCode: string) {
    let countriesPulldown: passportCountryPulldown[] = [];

    // POS国_国_国(国際化)全リストから値を取得するObservable
    const posCountryAll$ = this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.POS_COUNTRY_ALL.key);
    // 国_国(国際化)全リストから値を取得するObservable
    const countryCountryI18nAll$ = this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.COUNTRY_COUNTRYI18N_ALL.key);
    // 上記2つから値が流れてくるのを待ち受け、2つが揃ったら値を流すObservable
    const zip$ = zip([posCountryAll$, countryCountryI18nAll$]);

    this.subscribeService(
      zip$.pipe(take(1)),
      ([posCountryAll, countryCountryI18nAll]: [PosCountryAllData[], CountryCountryI18nAllData[]]) => {
        // 指定されたPOS国に属する国でAPIS/パスポートフラグがtrueである国を抽出
        const countries = posCountryAll.filter((country: PosCountryAllData) => {
          return country.pos_country_code === posCountryCode && country.apis_passport_flag;
        });

        // 抽出した国から、プルダウンの表示に必要なデータに変換
        countriesPulldown = countries.map((country) => {
          return {
            key: country.country_2letter_code,
            value: country.country_name,
          };
        });

        // 指定されたPOS国コードが日本以外の場合、国_国(国際)_全リストから日本の情報も付け加えて返却する
        if (posCountryCode !== 'JP') {
          const countryJp = countryCountryI18nAll.find((country) => country.country_2letter_code === 'JP');
          if (countryJp) {
            countriesPulldown.push({
              key: countryJp.country_2letter_code,
              value: countryJp.country_name,
            });
          }
        }
      }
    );

    return countriesPulldown;
  }

  /**
   * POS国コードが指定されたPOS国コードまたはJPに属さない国情報を返却する
   * 注：POS_国_国国際_全リストが読み込まれていない状態で呼び出すと空の配列を返す
   * @param posCountryCode
   */
  public getCountriesNotBelongToPosCountryAndJapan(posCountryCode: string) {
    let countriesPulldown: passportCountryPulldown[] = [];

    // POS国_国_国(国際化)全リストから値を取得するObservable
    const posCountryAll$ = this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.POS_COUNTRY_ALL.key);
    // 国_国(国際化)全リストから値を取得するObservable
    const countryCountryI18nAll$ = this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.COUNTRY_COUNTRYI18N_ALL.key);
    // 上記2つから値が流れてくるのを待ち受け、2つが揃ったら値を流すObservable
    const zip$ = zip([posCountryAll$, countryCountryI18nAll$]);

    this.subscribeService(
      zip$.pipe(take(1)),
      ([posCountryAll, countryCountryI18nAll]: [PosCountryAllData[], CountryCountryI18nAllData[]]) => {
        const exceptCountryCodes = posCountryAll
          .filter((country: PosCountryAllData) => {
            return country.pos_country_code === posCountryCode && country.apis_passport_flag;
          })
          .map((country) => country.country_2letter_code);
        exceptCountryCodes.push('JP');

        // 指定されたPOSと日本以外に属する国でAPIS/パスポートフラグがtrueである国を抽出
        const countries = countryCountryI18nAll.filter((country: CountryCountryI18nAllData) => {
          return !exceptCountryCodes.includes(country.country_2letter_code) && country.apis_passport_flag;
        });

        countriesPulldown = countries.map((country) => {
          return {
            key: country.country_2letter_code || country.country_3letter_code,
            value: country.country_name,
          };
        });
      }
    );

    return countriesPulldown;
  }

  /**
   * セグメントの出発日をマスターに格納されている適用開始日、適用終了日を比較できる形式(yyyyMMdd)に変換する
   * @param departureDate
   * @returns 変換された日付文字列
   */
  private formatDepartureDateCanCompare = (departureDate: string) => {
    const SplittedDepartureDate = departureDate.split('-');
    const year = SplittedDepartureDate[0];
    const month = SplittedDepartureDate[1];
    const day = SplittedDepartureDate[2].split('T')[0];

    return year + month + day;
  };

  /**
   * キャリアマスターから適用日を考慮して指定されたキャリアマスタのレコードを取得する
   * 注：キャリアマスターのキャッシュが読み込まれていない段階でメソッドを呼ぶと空文字列が返却される
   * @param operatingAirlineCode　運行キャリアコード
   * @param departureDate セグメントの出発日時
   * @returns 指定されたキャリアコードのレコード
   */
  public getOperatingAirlineRecord(
    operatingAirlineCode: string,
    departureDate: string
  ): AirlineI18nJoinData | undefined {
    // セグメントの出発時刻をyyyyMMdd表記の数値型にしたもの
    const departureDateNumber = Number(this.formatDepartureDateCanCompare(departureDate));
    // キャリアURL
    let record: AirlineI18nJoinData | undefined;
    this.subscribeService(
      this._aswMasterSvc.getAswMasterByKey$(LOAD_MASTERS.AIRLINE_I18NJOIN_BY_AIRLINE_CODE.key).pipe(take(1)),
      (carrierMaster) => {
        // 指定したキャリアコードから対象となるレコードのリストを引き当てる
        // 適用日が存在するので、適用日の種類分の要素数になる
        const carrierList = carrierMaster[operatingAirlineCode];
        if (!carrierList) {
          return;
        }

        // 適用日ごとに繰り返して、現在時刻が適用開始日から適用終了日までの間であればその要素のキャリアURLを設定する
        carrierList.forEach((carrierRecord: AirlineI18nJoinData) => {
          // 適用日はyyyyMMdd形式なのでnumber型に変換して8桁の数値として比較
          const apply_from_date = Number(carrierRecord.apply_from_date);
          const apply_to_date = Number(carrierRecord.apply_to_date);
          if (apply_from_date <= departureDateNumber && departureDateNumber <= apply_to_date) {
            // 適用開始日と適用終了日の範囲内に存在するレコードが見つかった場合それを格納して繰り返しを終了する
            record = carrierRecord;
            return;
          }
        });
      }
    );
    return record;
  }
}
